import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { HeaderPrimary } from "../../components/HeaderText/HeaderText.style";
import { authService } from "../../services/Services";
import "./ForgotPassword.css";

interface ForgotPasswordProps {
    setErrorState: (state: { hasError: boolean; message: string }) => void;
}

export default function ForgotPassword({ setErrorState }: ForgotPasswordProps) {
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();

    const validateForm = () => {
        return username.length > 0;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);

        try {
            await authService.forgotPassword(username);
            setSubmitted(true);
            // Navigate to reset password page with username
            navigate("/reset-password", { state: { username } });
        } catch (error) {
            setErrorState({
                hasError: true,
                message: error instanceof Error ? error.message : "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <Container className="ForgotPasswordContainer">
                <Box className="ForgotPasswordForm">
                    <HeaderPrimary>Check Your Email</HeaderPrimary>
                    <Box sx={{ mt: 3, mb: 3 }}>
                        <Alert severity="success">
                            If an account exists for {username}, you will receive a password reset code.
                        </Alert>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
                        <Link to="/reset-password" state={{ username }}>
                            Enter verification code
                        </Link>
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container className="ForgotPasswordContainer">
            <form onSubmit={handleSubmit} className="ForgotPasswordForm">
                <HeaderPrimary>Forgot Password</HeaderPrimary>
                <Typography variant="body2" sx={{ textAlign: "center", mt: 2, mb: 3 }}>
                    Enter your username and we'll send you a code to reset your password.
                </Typography>
                <Box display="flex" justifyContent="center">
                    <TextField
                        label="Username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                    />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: "#043c4a",
                            height: "50px",
                            minWidth: "150px",
                            color: "#ffffff",
                            ":hover": {
                                bgcolor: "#043c4a",
                            },
                        }}
                        disabled={!validateForm() || loading}
                        type="submit"
                    >
                        {loading ? "Sending..." : "Send Code"}
                    </Button>
                </Box>
                <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Typography variant="body2">
                        Remember your password? <Link to="/login">Back to Login</Link>
                    </Typography>
                </Box>
            </form>
        </Container>
    );
}
